import configurationService from 'Services/ConfigurationService'
import hostedPaymentPagesService from 'Services/HostedPaymentPageService'

// initial state
const state = {
  version: null,
  configurations: []
}

// getters
const getters = {
  configuration: state => configurationId => state.configurations.find(configuration => configuration.configurationId === configurationId) || {},
  hostedPaymentPages: (state, getters) => configurationId => getters.configuration(configurationId).hostedPages || [],
  hostedPaymentPage: (state, getters) => (configurationId, reference) => getters.hostedPaymentPages(configurationId).find(hostedPage => hostedPage.reference === reference) || {}
}

const actions = {
  filterConfigurations: ({ commit }, { context, filterContext }) => {
    const { request, cancelToken } = configurationService.getAll(context, filterContext)

    const promise = new Promise((resolve, reject) => {
      request.then(result => {
        commit('setConfigurations', result.data)
        resolve(result)
      }).catch(error => {
        reject(error)
      })
    })

    return { promise, cancelToken }
  },

  getConfigurations: ({ dispatch }, { context }) => {
    return dispatch('filterConfigurations', { context }).then(({ promise }) => promise.then(result => result.data))
  },

  getConfiguration: ({ commit, state, getters }, { context }) => {
    return new Promise((resolve, reject) => {
      configurationService.get(context).then(result => {
        commit('setVersion', result.data.version)

        if (state.configurations.some(item => item.configurationId === result.data.configurationId)) {
          commit('updateConfiguration', { ...result.data, hostedPages: getters.configuration(context.configuration.id).hostedPages || [] })
        } else {
          commit('setConfiguration', result.data)
        }

        resolve(result.data)
      }).catch(error => {
        reject(error)
      })
    })
  },

  addConfiguration: ({ dispatch }, { context, configuration }) => {
    return new Promise((resolve, reject) => {
      configurationService.add(context, configuration).then(result => {
        context.configuration = { id: result.configurationId }
        return dispatch('getConfiguration', { context }).then(result => resolve(result))
      }).catch(error => {
        reject(error)
      })
    })
  },

  updateConfiguration: ({ state, dispatch }, { context, configuration }) => {
    return new Promise((resolve, reject) => {
      configurationService.update(context, { ...configuration, version: state.version }).then(() => {
        return dispatch('getConfiguration', { context }).then(result => resolve(result))
      }).catch(error => {
        reject(error)
      })
    })
  },

  deleteConfiguration: ({ state, commit }, { context }) => {
    return new Promise((resolve, reject) => {
      configurationService.delete(context).then(() => {
        commit('setVersion', state.version + 1)
        commit('deleteConfiguration', context.configuration.id)
        resolve()
      }).catch(error => {
        reject(error)
      })
    })
  },

  getPaymentMethodConfiguration: ({ commit, getters }, { context, reference }) => {
    return new Promise((resolve, reject) => {
      configurationService.getPaymentMethodConfiguration(context, reference).then(result => {
        const configuration = getters.configuration(context.configuration.id)
        const paymentMethodConfiguration = configuration.paymentMethods.find(method => method.paymentMethod === reference)
        Object.assign(paymentMethodConfiguration, { configuration: result.data })

        commit('setVersion', result.data.version)
        commit('updateConfiguration', configuration)
        resolve(paymentMethodConfiguration)
      }).catch(error => {
        reject(error)
      })
    })
  },

  updatePaymentMethodConfiguration: ({ state, dispatch }, { context, reference, configuration }) => {
    return new Promise((resolve, reject) => {
      configurationService.updatePaymentMethodConfiguration(context, reference, { ...configuration, version: state.version }).then(() => {
        dispatch('getPaymentMethodConfiguration', { context, reference }).then(configuration => {
          resolve(configuration)
        })
      }).catch(error => {
        reject(error)
      })
    })
  },

  getHostedPaymentPages: ({ commit, getters }, { context }) => {
    const { request, cancelToken } = hostedPaymentPagesService.getAll(context)

    const promise = new Promise((resolve, reject) => {
      request.then(result => {
        const { hostedPages = [], version } = result.data

        commit('setVersion', version)
        commit('updateConfiguration', { ...getters.configuration(context.configuration.id), hostedPages })

        resolve(hostedPages)
      }).catch(error => {
        reject(error)
      })
    })

    return { promise, cancelToken }
  },

  getHostedPaymentPage: ({ commit, getters }, { context, reference }) => {
    return new Promise((resolve, reject) => {
      hostedPaymentPagesService.get(context, reference).then(result => {
        const { hostedPage, version } = result.data

        const hostedPages = getters.hostedPaymentPages(context.configuration.id).some(item => item.reference === hostedPage.reference)
          ? getters.hostedPaymentPages(context.configuration.id).map(item => item.reference === hostedPage.reference ? hostedPage : item)
          : [...getters.hostedPaymentPages(context.configuration.id), hostedPage]

        commit('setVersion', version)
        commit('updateConfiguration', { ...getters.configuration(context.configuration.id), hostedPages })

        resolve(hostedPage)
      }).catch(error => {
        reject(error)
      })
    })
  },

  addHostedPaymentPage: ({ dispatch }, { context, hostedPaymentPage }) => {
    return new Promise((resolve, reject) => {
      hostedPaymentPagesService.add(context, hostedPaymentPage).then((result) => {
        return dispatch('getHostedPaymentPage', { context, reference: result.data.reference }).then(resolve)
      }).catch(error => {
        reject(error)
      })
    })
  },

  updateHostedPaymentPage: ({ state, dispatch }, { context, reference, hostedPaymentPage }) => {
    return new Promise((resolve, reject) => {
      hostedPaymentPagesService.update(context, reference, { ...hostedPaymentPage, version: state.version }).then(() => {
        return dispatch('getHostedPaymentPage', { context, reference }).then(resolve)
      }).catch(error => {
        reject(error)
      })
    })
  },

  deleteHostedPaymentPage: ({ state, commit, getters }, { context, reference }) => {
    return new Promise((resolve, reject) => {
      hostedPaymentPagesService.delete(context, reference).then(() => {
        commit('setVersion', state.version + 1)
        commit('updateConfiguration', { ...getters.configuration(context.configuration.id), hostedPages: getters.hostedPaymentPages(context.configuration.id).filter(item => item.reference !== reference) })

        resolve()
      }).catch(error => {
        reject(error)
      })
    })
  }
}

// mutations
const mutations = {
  setVersion (state, version) {
    if (version) {
      state.version = version
    }
  },

  setConfigurations (state, configurations) {
    state.configurations = configurations
  },

  setConfiguration (state, configuration) {
    state.configurations.push(configuration)
  },

  updateConfiguration (state, configuration) {
    state.configurations = state.configurations.map(item =>
      item.configurationId === configuration.configurationId ? configuration : item
    )
  },

  deleteConfiguration (state, configurationId) {
    state.configurations = state.configurations.filter(configuration => configuration.configurationId !== configurationId)
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
